import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IndicatorComponent } from '@kinet-open-network/client/shared/ui';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { Observable, map, of } from 'rxjs';

interface SelectProps extends FormlyFieldProps, FormlyFieldSelectProps {
  onFilter?: (field: FormlyFieldConfig, event: unknown) => void;

  /**
   * If set to true, the current value will be added to the options if the options are empty.
   * This is useful when the options are loaded asynchronously or they are not loaded on
   * purpose (e.g. when user doesn't have ability to load them) but the value itself is already set.
   *
   * With this option enabled, dropdown will show list of options with the current value
   * as the only option in the list. This cannot be used when the value and label are different,
   * because value is used as a label.
   *
   * @default false
   */
  appendValueOption?: boolean;
  filter: boolean;
  icon?: string;
}

export interface FormlySelectFieldConfig
  extends FormlyFieldConfig<SelectProps> {}

type SelectOption = {
  label: string;
  value: string | number;
  chips?: {
    label: string;
    icon?: string;
    color?: string;
    style?: Record<string, string>;
    class?: string;
  }[];
};

@Component({
  selector: 'kon-select-type',
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    DropdownModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    IndicatorComponent,
    ChipModule,
  ],
  template: `
    <p-dropdown
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(selectOptions | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [formControl]="formControl"
      [showClear]="!props.required"
      (onChange)="props.change && props.change(field, $event)"
      [virtualScroll]="true"
      [filter]="props.filter"
      [virtualScrollItemSize]="42"
      [style]="{ width: '100%' }"
      (onFilter)="props.onFilter && props.onFilter(field, $event)"
      [placeholder]="props.placeholder"
    >
      <ng-template let-item pTemplate="selectedItem">
        @if (item) {
        <div class="flex items-center gap-2">
          @if (item.color) {
          <kon-indicator [color]="item.color" />
          } @if (item.icon) {
          <i [class]="item.icon" [ngStyle]="icontStyle(item)"></i>
          }
          <div class="truncate" [title]="item.label">{{ item.label }}</div>
          @for (chip of item?.chips ?? []; track $index) {
          <p-chip
            [label]="chip.label"
            [icon]="chip.icon"
            [style]="chip.style ?? { backgroundColor: chip.color }"
            [ngClass]="chip.class"
            class="-my-2"
          />
          }
        </div>
        }
      </ng-template>
      <ng-template let-item pTemplate="item">
        <div class="flex items-center gap-2">
          @if (item.color) {
          <kon-indicator [color]="item.color" />
          } @if (item.icon) {
          <i [class]="item.icon" [ngStyle]="icontStyle(item)"></i>
          }
          <div class="truncate" [title]="item.label">{{ item.label }}</div>
          @for (chip of item?.chips ?? []; track $index) {
          <p-chip
            [label]="chip.label"
            [icon]="chip.icon"
            [style]="chip.style ?? { backgroundColor: chip.color }"
            [ngClass]="chip.class"
            class="-my-2"
          />
          }
        </div>
      </ng-template>
    </p-dropdown>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySelectTypeComponent extends FieldType<
  FieldTypeConfig<SelectProps>
> {
  override defaultOptions = {
    props: {
      appendValueOption: false,
      filter: true,
    },
  };

  get selectOptions() {
    if (this.props.options) {
      if (this.props.options instanceof Observable) {
        return this.props.options.pipe(
          map((options) => this.appendValueOption(options)),
        );
      }

      return of(this.appendValueOption(this.props.options));
    }

    return of([]);
  }

  /**
   * This method will add current value to the options if the options are empty,
   * this is useful when the options are loaded asynchronously and the value is already set.
   * This only works when the value and label are the same, because value is used as a label.
   *
   * @param options
   * @returns options with current value if the options are empty
   */
  private appendValueOption(
    options: SelectOption[] | undefined,
  ): SelectOption[] {
    const value = this.formControl.value;
    if (this.props.appendValueOption && value) {
      const isOptionPresent = options?.some((option) => option.value === value);
      if (!isOptionPresent) {
        return [
          {
            label: value,
            value,
          },
          ...(options ?? []),
        ];
      }
    }
    return options ?? [];
  }

  icontStyle(item: any) {
    const iconColor = item.iconColor;
    return iconColor ? { color: iconColor } : undefined;
  }
}
